import { useState, useRef } from 'react'
import { Button } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Checkbox } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { Alert, AlertDescription } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Wand2, Upload, Check, X, Loader2 } from 'lucide-react'
import type { SkillAnalysisResult } from '@creanizer/types'
import { useGenerator } from '../../../../hooks/useGenerator'
import { readMultipleFiles, FILE_ACCEPT } from '../../utils/fileReader'

interface SkillAnalyzerProps {
  systemName: string
  onImport: (skills: SkillAnalysisResult['skills']) => void
  onCancel: () => void
}

export function SkillAnalyzer({ systemName, onImport, onCancel }: SkillAnalyzerProps) {
  const [sourceType, setSourceType] = useState<'rulebook' | 'skill_list' | 'description' | 'mixed'>('skill_list')
  const [sourceText, setSourceText] = useState('')
  const [extractBaseValues, setExtractBaseValues] = useState(true)
  const [extractCategories, setExtractCategories] = useState(true)
  const [extractRelatedStats, setExtractRelatedStats] = useState(true)
  const [useGemini3Mode, setUseGemini3Mode] = useState(false)
  const [analysisResult, setAnalysisResult] = useState<SkillAnalysisResult | null>(null)
  const [selectedSkills, setSelectedSkills] = useState<Set<number>>(new Set())
  const [isUploading, setIsUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const generator = useGenerator('scenario')

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files) return
    setIsUploading(true)
    try {
      const { combinedText } = await readMultipleFiles(files)
      setSourceText(prev => prev ? prev + '\n\n' + combinedText : combinedText)
    } finally {
      setIsUploading(false)
      if (e.target) e.target.value = ''
    }
  }

  const handleAnalyze = async () => {
    if (!sourceText.trim()) return
    try {
      const response = await generator.mutateAsync({
        mode: 'analyze_skills',
        skillAnalysisSettings: {
          sourceType,
          sourceText,
          systemName,
          extractBaseValues,
          extractCategories,
          extractRelatedStats,
          useGemini3Mode
        }
      })
      const result = response.result as SkillAnalysisResult
      setAnalysisResult(result)
      setSelectedSkills(new Set(result.skills.map((_, i) => i)))
    } catch (error) {
      console.error('Skill analysis failed:', error)
    }
  }

  const toggleSkill = (index: number) => {
    const newSet = new Set(selectedSkills)
    if (newSet.has(index)) {
      newSet.delete(index)
    } else {
      newSet.add(index)
    }
    setSelectedSkills(newSet)
  }

  const handleImport = () => {
    if (!analysisResult) return
    const skills = analysisResult.skills.filter((_, i) => selectedSkills.has(i))
    onImport(skills)
  }

  const selectAll = () => {
    if (analysisResult) {
      setSelectedSkills(new Set(analysisResult.skills.map((_, i) => i)))
    }
  }

  const selectNone = () => {
    setSelectedSkills(new Set())
  }

  if (analysisResult) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-medium">解析結果: {analysisResult.skills.length}件のスキル</h3>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={selectAll}>全選択</Button>
            <Button size="sm" variant="outline" onClick={selectNone}>全解除</Button>
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto space-y-1 border rounded-lg p-2">
          {analysisResult.skills.map((skill, index) => (
            <div
              key={index}
              className={`p-2 rounded cursor-pointer flex items-start gap-2 ${
                selectedSkills.has(index) ? 'bg-blue-50 border border-blue-200' : 'hover:bg-gray-50'
              }`}
              onClick={() => toggleSkill(index)}
            >
              <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 mt-0.5 ${
                selectedSkills.has(index) ? 'bg-blue-500 border-blue-500' : 'border-gray-300'
              }`}>
                {selectedSkills.has(index) && <Check className="w-3 h-3 text-white" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium">{skill.name}</span>
                  {skill.category && <Badge variant="outline" className="text-xs">{skill.category}</Badge>}
                  {skill.baseValue !== undefined && (
                    <Badge variant="secondary" className="text-xs">基本値: {skill.baseValue}</Badge>
                  )}
                </div>
                {skill.description && (
                  <p className="text-sm text-gray-600 truncate">{skill.description}</p>
                )}
              </div>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Button onClick={handleImport} disabled={selectedSkills.size === 0}>
            <Check className="w-4 h-4 mr-1" />
            {selectedSkills.size}件をインポート
          </Button>
          <Button variant="outline" onClick={() => setAnalysisResult(null)}>
            やり直す
          </Button>
          <Button variant="ghost" onClick={onCancel}>キャンセル</Button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div>
        <Label>ソースタイプ</Label>
        <Select value={sourceType} onValueChange={(v) => setSourceType(v as any)}>
          <SelectTrigger className="mt-1">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="skill_list">スキルリスト</SelectItem>
            <SelectItem value="rulebook">ルールブック</SelectItem>
            <SelectItem value="description">説明文</SelectItem>
            <SelectItem value="mixed">混合</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div>
        <Label>解析対象テキスト</Label>
        <div className="flex gap-2 mt-1 mb-2">
          <input
            ref={fileInputRef}
            type="file"
            accept={FILE_ACCEPT}
            multiple
            onChange={handleFileUpload}
            className="hidden"
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
          >
            <Upload className="w-4 h-4 mr-1" />ファイル読込
          </Button>
        </div>
        <Textarea
          value={sourceText}
          onChange={(e) => setSourceText(e.target.value)}
          placeholder="ルールブックやスキルリストのテキストを貼り付け..."
          rows={8}
          className="font-mono text-sm"
        />
        <p className="text-xs text-gray-500 mt-1">{sourceText.length.toLocaleString()}文字</p>
      </div>

      <div className="space-y-2">
        <Label className="text-sm">抽出オプション</Label>
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center gap-2">
            <Checkbox
              id="extractBaseValues"
              checked={extractBaseValues}
              onCheckedChange={(c) => setExtractBaseValues(!!c)}
            />
            <Label htmlFor="extractBaseValues" className="text-sm">基本値</Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="extractCategories"
              checked={extractCategories}
              onCheckedChange={(c) => setExtractCategories(!!c)}
            />
            <Label htmlFor="extractCategories" className="text-sm">カテゴリ</Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="extractRelatedStats"
              checked={extractRelatedStats}
              onCheckedChange={(c) => setExtractRelatedStats(!!c)}
            />
            <Label htmlFor="extractRelatedStats" className="text-sm">関連能力値</Label>
          </div>
        </div>
        <div className="flex items-center gap-2 pt-2">
          <Checkbox
            id="useGemini3"
            checked={useGemini3Mode}
            onCheckedChange={(c) => setUseGemini3Mode(!!c)}
          />
          <Label htmlFor="useGemini3" className="text-sm">Gemini 3 モード（有料APIのみ）</Label>
        </div>
      </div>

      {generator.error && (
        <Alert variant="destructive">
          <AlertDescription>解析に失敗しました: {String(generator.error)}</AlertDescription>
        </Alert>
      )}

      <div className="flex gap-2">
        <Button onClick={handleAnalyze} disabled={!sourceText.trim() || generator.isPending}>
          {generator.isPending ? (
            <><Loader2 className="w-4 h-4 mr-1 animate-spin" />解析中...</>
          ) : (
            <><Wand2 className="w-4 h-4 mr-1" />AIで解析</>
          )}
        </Button>
        <Button variant="ghost" onClick={onCancel}>キャンセル</Button>
      </div>
    </div>
  )
}
